// Public invoice lookup — the one thing an anonymous visitor may call.
//
// SECURITY CHANGE from the Firestore build. There, the page fetched the whole
// transaction document and compared the phone's last 4 digits in the browser:
// the invoice was already on the wire before the check ran, so anyone with the
// link could read it straight out of devtools regardless of the PIN.
//
// Now the check happens inside get_public_invoice(). Nothing is returned until
// the digits match, `transactions` itself stays unreadable to anon, and the
// customer's email is stripped from the response.

import { supabase } from '@/lib/supabase';

export interface PublicInvoiceItem {
  name: string;
  qty: number;
  price: number;
  subtotal: number;
  returnedQty: number;
  promoDiscount?: number;
  /** Batch as printed on the packaging. Null when none was recorded. */
  lotNumber: string | null;
  /** ISO date, null when the product does not expire. */
  expiryDate: string | null;
  unit: string | null;
}

export interface PublicInvoiceData {
  invoiceNumber: string;
  createdAt: string;
  customer: { name?: string; phone?: string; company?: string };
  items: PublicInvoiceItem[];
  subtotal: number;
  /** Promo money on the sale; `appliedPromos` names each (0022 invoices: 0 / []). */
  promoDiscount?: number;
  appliedPromos?: { promo_id: string; kind: string; name: string; amount: number }[];
  discountAmount: number;
  taxAmount: number;
  taxRate: number;
  grandTotal: number;
  paidAmount: number;
  outstanding: number;
  dueDate: string | null;
  paymentMethod: string;
  fulfillment: string;
  paymentStatus: string;
  company: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
    npwp?: string | null;
    logoUrl?: string | null;
  };
}

export class InvoiceVerificationError extends Error {}

/**
 * Fetch an invoice after verifying the last 4 digits of the customer's phone.
 * Throws InvoiceVerificationError when the digits are wrong OR the invoice
 * does not exist — the server returns the same message for both, so a stranger
 * cannot use the endpoint to discover which invoice ids are real.
 */
export async function fetchPublicInvoice(
  transactionId: string,
  phoneLast4: string,
): Promise<PublicInvoiceData> {
  const { data, error } = await supabase.rpc('get_public_invoice', {
    p_transaction_id: transactionId,
    p_phone_last4: phoneLast4,
  });
  if (error) throw new InvoiceVerificationError(error.message);
  if (!data) throw new InvoiceVerificationError('Verifikasi gagal');
  return data as unknown as PublicInvoiceData;
}
